import { useCallback, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { useAppStore } from "../../../stores/store";
import {
  AI_PLAN_DEFAULT_OBJECT_TYPE_CODE,
  AUTO_TAGGING_START_FAILED_TOAST,
  AUTO_TAGGING_START_SUCCESS_TOAST,
  START_AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  enrichAutoTaggingPlans,
  fetchAllStartAutoTaggingPlanIds,
  fetchConfigurableConcepts,
  fetchStartAutoTaggingPlans,
  triggerAutoTaggingBundleTask,
  type AutoTaggingBundleTaskRegulation,
  type AutoTaggingPlan,
  type ConfigurableConcept,
} from "../../../services/autoTaggingService";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import AutoTaggingPlansTable from "./AutoTaggingPlansTable";

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: START_AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
};

const NUNITO = '"Nunito Sans", sans-serif';

function toGridRowSelectionModel(ids: GridRowId[]): GridRowSelectionModel {
  return {
    type: "include",
    ids: new Set(ids),
  };
}

function getModelIdSet(model: GridRowSelectionModel): Set<GridRowId> {
  if (Array.isArray(model)) {
    return new Set(model);
  }
  if (model && typeof model === "object" && "ids" in model) {
    const { ids } = model;
    if (ids instanceof Set) {
      return ids;
    }
    if (Array.isArray(ids)) {
      return new Set(ids);
    }
  }
  return new Set();
}

function getSelectedIdsFromModel(model: GridRowSelectionModel): GridRowId[] {
  if (Array.isArray(model)) {
    return model;
  }
  if (
    model &&
    typeof model === "object" &&
    "type" in model &&
    model.type === "exclude"
  ) {
    // Exclude models are normalized on change; treat unresolved ones as empty.
    return [];
  }
  return Array.from(getModelIdSet(model));
}

function isHeaderSelectAllModel(model: GridRowSelectionModel): boolean {
  if (Array.isArray(model) || !model || typeof model !== "object") {
    return false;
  }
  return model.type === "exclude" && getModelIdSet(model).size === 0;
}

function isHeaderDeselectAllModel(model: GridRowSelectionModel): boolean {
  if (Array.isArray(model)) {
    return model.length === 0;
  }
  if (!model || typeof model !== "object" || !("type" in model)) {
    return false;
  }
  return model.type === "include" && getModelIdSet(model).size === 0;
}

/**
 * DataGrid header select-all emits `{ type: "exclude", ids }` (ids = deselected).
 * Partial include/exclude updates keep off-page ids from the previous model.
 */
function normalizeSelectionModel(
  model: GridRowSelectionModel,
  pageRowIds: GridRowId[],
  previousModel: GridRowSelectionModel,
): GridRowSelectionModel {
  const pageIdSet = new Set(pageRowIds.map(String));
  const previousOffPage = getSelectedIdsFromModel(previousModel).filter(
    (id) => !pageIdSet.has(String(id)),
  );

  if (Array.isArray(model)) {
    return normalizeIncludeSelection(
      new Set(model),
      pageRowIds,
      pageIdSet,
      previousOffPage,
    );
  }
  if (!model || typeof model !== "object" || !("type" in model)) {
    return toGridRowSelectionModel([...previousOffPage]);
  }

  if (model.type === "exclude") {
    const excluded = getModelIdSet(model);
    const selectedOnPage = pageRowIds.filter(
      (id) => !excluded.has(id) && !excluded.has(String(id)),
    );
    return toGridRowSelectionModel([...previousOffPage, ...selectedOnPage]);
  }

  return normalizeIncludeSelection(
    getModelIdSet(model),
    pageRowIds,
    pageIdSet,
    previousOffPage,
  );
}

function normalizeIncludeSelection(
  included: Set<GridRowId>,
  pageRowIds: GridRowId[],
  pageIdSet: Set<string>,
  previousOffPage: GridRowId[],
): GridRowSelectionModel {
  const selectedOnPage = pageRowIds.filter(
    (id) => included.has(id) || included.has(String(id)),
  );
  const includedOffPage = Array.from(included).filter(
    (id) => !pageIdSet.has(String(id)),
  );
  // When the grid drops off-page ids on a partial update, keep them.
  const selectedOffPage =
    includedOffPage.length > 0 ? includedOffPage : previousOffPage;
  return toGridRowSelectionModel([...selectedOffPage, ...selectedOnPage]);
}

export type StartAutoTaggingDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  regulations: AutoTaggingBundleTaskRegulation[];
}>;

export default function StartAutoTaggingDialog({
  open,
  onClose,
  regulations,
}: StartAutoTaggingDialogProps) {
  const { showMessage } = useSnackbar();
  const canExecuteAutoTagging = useHasPrivilege("gri.auto_tagging.execute");
  const privilegesLoaded =
    useAppStore((state) => state.ui?.privilegesLoaded) ?? false;
  const [plans, setPlans] = useState<AutoTaggingPlan[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    DEFAULT_PAGINATION_MODEL,
  );
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    () => toGridRowSelectionModel([]),
  );
  const [starting, setStarting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const conceptsRef = useRef<ConfigurableConcept[]>([]);
  const loadRequestIdRef = useRef(0);
  const selectAllRequestIdRef = useRef(0);

  const loadConcepts = useCallback(async () => {
    const conceptsData = await fetchConfigurableConcepts(
      AI_PLAN_DEFAULT_OBJECT_TYPE_CODE,
    );
    conceptsRef.current = conceptsData;
    return conceptsData;
  }, []);

  const loadPlans = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      // Enrich Impact item labels with concept display names when available.
      const conceptsPromise =
        conceptsRef.current.length > 0
          ? Promise.resolve(conceptsRef.current)
          : loadConcepts();
      const plansPromise = fetchStartAutoTaggingPlans({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      });
      const [conceptsData, { items, total }] = await Promise.all([
        conceptsPromise,
        plansPromise,
      ]);
      if (requestId !== loadRequestIdRef.current) return;
      const enriched = enrichAutoTaggingPlans(items, conceptsData);
      setPlans(enriched);
      setRowCount(total);
      setHasLoadedOnce(true);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      showMessage(
        err instanceof Error ? err.message : "Failed to load assessment plans",
        "error",
      );
      setPlans([]);
      setRowCount(0);
      setHasLoadedOnce(true);
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [loadConcepts, paginationModel.page, paginationModel.pageSize, showMessage]);

  useEffect(() => {
    if (!open) {
      setPaginationModel(DEFAULT_PAGINATION_MODEL);
      selectAllRequestIdRef.current += 1;
      setSelectingAll(false);
      return;
    }
    setSelectionModel(toGridRowSelectionModel([]));
    setPlans([]);
    setRowCount(0);
    setHasLoadedOnce(false);
    setSelectingAll(false);
    selectAllRequestIdRef.current += 1;
    conceptsRef.current = [];
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!privilegesLoaded) return;
    if (!canExecuteAutoTagging) {
      setPlans([]);
      setRowCount(0);
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }
    void loadPlans();
  }, [open, privilegesLoaded, canExecuteAutoTagging, loadPlans]);

  const handleSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (isHeaderSelectAllModel(model)) {
        const requestId = ++selectAllRequestIdRef.current;
        setSelectingAll(true);
        void (async () => {
          try {
            const allIds = await fetchAllStartAutoTaggingPlanIds();
            if (requestId !== selectAllRequestIdRef.current) return;
            setSelectionModel(toGridRowSelectionModel(allIds));
          } catch (err) {
            if (requestId !== selectAllRequestIdRef.current) return;
            showMessage(
              err instanceof Error
                ? err.message
                : "Failed to select all assessment plans",
              "error",
            );
          } finally {
            if (requestId === selectAllRequestIdRef.current) {
              setSelectingAll(false);
            }
          }
        })();
        return;
      }

      if (isHeaderDeselectAllModel(model)) {
        selectAllRequestIdRef.current += 1;
        setSelectingAll(false);
        setSelectionModel(toGridRowSelectionModel([]));
        return;
      }

      setSelectionModel((prev) =>
        normalizeSelectionModel(
          model,
          plans.map((plan) => plan.id),
          prev,
        ),
      );
    },
    [plans, showMessage],
  );

  const selectedCount = getSelectedIdsFromModel(selectionModel).length;

  const handleStart = useCallback(async () => {
    const planIds = getSelectedIdsFromModel(selectionModel)
      .map((id) => String(id).trim())
      .filter((id) => id.length > 0);
    if (
      !canExecuteAutoTagging ||
      regulations.length === 0 ||
      planIds.length === 0 ||
      starting ||
      selectingAll
    ) {
      return;
    }
    setStarting(true);
    try {
      await triggerAutoTaggingBundleTask({
        regulations,
        plan_ids: planIds,
        force_update: true,
      });
      showMessage(AUTO_TAGGING_START_SUCCESS_TOAST, "success");
      onClose();
    } catch (err) {
      showMessage(
        err instanceof Error ? err.message : AUTO_TAGGING_START_FAILED_TOAST,
        "error",
      );
    } finally {
      setStarting(false);
    }
  }, [
    canExecuteAutoTagging,
    onClose,
    regulations,
    selectionModel,
    selectingAll,
    showMessage,
    starting,
  ]);

  const selectedPlanCount = getSelectedIdsFromModel(selectionModel).length;
  const startDisabled =
    !canExecuteAutoTagging ||
    starting ||
    loading ||
    selectingAll ||
    regulations.length === 0 ||
    selectedPlanCount === 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(onClose)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 1248,
            maxWidth: "95vw",
            maxHeight: "90vh",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #F2F2F2",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "25px",
              color: "#0D0D0D",
            }}
          >
            Run AI Assessment
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={starting}
            sx={DIALOG_CLOSE_BUTTON_SX}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pt: "24px !important", pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                px: "9px",
                py: "9px",
                borderRadius: "4px",
                bgcolor: "#F1F5F9",
                border: "1px solid #E2E8F0",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {selectingAll ? <CircularProgress size={12} /> : null}
              <Typography
                sx={{
                  fontFamily: NUNITO,
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: "12px",
                  color: "#475569",
                }}
              >
                {selectedCount} Selected
              </Typography>
            </Box>
          </Box>

          {privilegesLoaded && !canExecuteAutoTagging ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f9fafb",
                borderRadius: "8px",
                border: "1px dashed #d1d5db",
                minHeight: 320,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "#374151", fontWeight: 600 }}>
                Access Denied
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
                You do not have permission to view assessment plans.
              </Typography>
            </Box>
          ) : !privilegesLoaded || (!hasLoadedOnce && loading) ? (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <AutoTaggingPlansTable
              rows={plans}
              rowCount={rowCount}
              loading={loading || selectingAll}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              selectionModel={selectionModel}
              onSelectionModelChange={handleSelectionModelChange}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 1.5,
            gap: 1.5,
            borderTop: "1px solid #F2F2F2",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={starting}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              fontFamily: '"Nunito Sans", sans-serif',
              fontSize: 14,
              height: 36,
            }}
          >
            Cancel
          </Button>
          <Tooltip
            title={canExecuteAutoTagging ? "" : "no permission"}
            arrow
            disableHoverListener={canExecuteAutoTagging}
          >
            <span>
              <Button
                variant="contained"
                disabled={startDisabled}
                onClick={() => void handleStart()}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontSize: 14,
                  height: 36,
                  boxShadow: "none",
                }}
              >
                {starting ? "Starting…" : "Start"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
}
